import { useState } from 'react';
import {
  Bell,
  User,
  Lock,
  Monitor,
  Sliders,
  Database,
  Check,
  Save,
  Smartphone,
  Globe,
  Building,
  FileText,
  AlertTriangle,
  Link,
  Key,
  Download,
  Camera,
  Moon,
  Sun
} from 'lucide-react';
import Footer from '../common/Footer';
import { useTheme } from '../../context/ThemeContext';
import { getThemeClasses } from '../../utils/theme';

// ============================================
// SETTINGS PAGE COMPONENT
// ============================================

const SettingsPage = () => {
  const { isDarkMode, toggleTheme, setTheme } = useTheme();
  const theme = getThemeClasses(isDarkMode);
  const [activeSection, setActiveSection] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Profile State
  const [profile, setProfile] = useState({
    firstName: 'Sarah',
    lastName: 'Mitchell',
    email: 'sarah.mitchell@agency.gov',
    phone: '(202) 555-0147',
    title: 'Senior Financial Auditor',
    department: 'Payment Integrity Division',
    employeeId: 'EMP-2847',
    region: 'Region 3'
  });

  // Notification Settings State
  const [notifications, setNotifications] = useState({
    emailNewAssignments: true,
    emailHighPriority: true,
    emailDailyDigest: false,
    emailWeeklyReport: true,
    emailSLAWarnings: true,
    inAppNewAssignments: true,
    inAppHighPriority: true,
    inAppMentions: true,
    inAppSystemUpdates: false,
    pushEnabled: false,
    quietHoursEnabled: true,
    quietHoursStart: '18:00',
    quietHoursEnd: '08:00'
  });

  // Display Settings State
  const [display, setDisplay] = useState({
    density: 'comfortable',
    sidebarCollapsed: false,
    showRiskScoreColors: true,
    highlightFlaggedCodes: true,
    defaultQueueView: 'all',
    itemsPerPage: '25',
    dateFormat: 'MM/DD/YYYY',
    currencyFormat: 'USD',
    timezone: 'America/New_York'
  });

  // Workflow Settings State
  const [workflow, setWorkflow] = useState({
    autoAssignEnabled: true,
    maxQueueSize: '50',
    priorityWeighting: 'balanced',
    slaReminderMinutes: '60',
    autoSaveNotes: true,
    requireNotesOnDeny: true,
    requireCategoryOnDeny: true,
    showSimilarCases: true,
    defaultConfidence: 'MEDIUM',
    quickDecisionMode: false
  });

  // Security Settings State
  const [security, setSecurity] = useState({
    mfaEnabled: true,
    mfaMethod: 'authenticator',
    sessionTimeout: '30',
    rememberDevice: true,
    loginNotifications: true,
    showLastLogin: true
  });

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    }, 1200);
  };

  const sections = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'display', label: 'Display', icon: isDarkMode ? Moon : Sun },
    { id: 'workflow', label: 'Workflow', icon: Sliders },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'integrations', label: 'Integrations', icon: Link },
    { id: 'data', label: 'Data & Privacy', icon: Database }
  ];

  const Toggle = ({ enabled, onChange, disabled = false }) => (
    <button
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`relative w-11 h-6 rounded-full transition-colors ${
        enabled ? 'bg-blue-600' : isDarkMode ? 'bg-slate-600' : 'bg-slate-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );

  const SettingRow = ({ label, description, children }) => (
    <div className={`flex items-center justify-between py-4 border-b ${theme.borderLight} last:border-0`}>
      <div className="flex-1 pr-4">
        <p className={`text-sm font-medium ${theme.textPrimary}`}>{label}</p>
        {description && <p className={`text-xs ${theme.textTertiary} mt-0.5`}>{description}</p>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );

  const SectionCard = ({ title, description, children }) => (
    <div className={`${theme.card} rounded-xl border shadow-sm overflow-hidden`}>
      <div className={`p-5 border-b ${theme.border}`}>
        <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>{title}</h3>
        {description && <p className={`text-sm ${theme.textTertiary} mt-1`}>{description}</p>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );

  return (
    <div className={`min-h-screen ${theme.bgPrimary} flex flex-col`}>
      <div className="flex-1">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>Settings</h2>
            <p className={`${theme.textTertiary} mt-1`}>Manage your account preferences and system configuration</p>
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-70"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Saving...
              </>
            ) : saveSuccess ? (
              <>
                <Check className="w-4 h-4" />
                Saved!
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>

        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <div className="w-64 flex-shrink-0">
            <div className={`${theme.card} rounded-xl border shadow-sm overflow-hidden sticky top-24`}>
              <nav className="p-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition ${
                      activeSection === section.id
                        ? 'bg-blue-600 text-white'
                        : `${theme.textSecondary} ${theme.bgHover}`
                    }`}
                  >
                    <section.icon className="w-5 h-5" />
                    <span className="font-medium">{section.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Settings Content */}
          <div className="flex-1 space-y-6">
            {/* Profile Section */}
            {activeSection === 'profile' && (
              <>
                <SectionCard title="Profile Information" description="Your personal and work details">
                  <div className="flex items-start gap-6 mb-6 pb-6 border-b border-slate-100">
                    <div className="relative">
                      <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl font-bold text-blue-600">SM</span>
                      </div>
                      <button className="absolute bottom-0 right-0 w-8 h-8 bg-white border border-slate-200 rounded-full flex items-center justify-center shadow-sm hover:bg-slate-50 transition">
                        <Camera className="w-4 h-4 text-slate-600" />
                      </button>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">{profile.firstName} {profile.lastName}</h4>
                      <p className="text-sm text-slate-500">{profile.title}</p>
                      <p className="text-sm text-slate-500">{profile.department}</p>
                      <button className="mt-2 text-sm text-blue-600 hover:text-blue-700">
                        Change photo
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
                      <input
                        type="text"
                        value={profile.firstName}
                        onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
                      <input
                        type="text"
                        value={profile.lastName}
                        onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={profile.email}
                        onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-slate-50"
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={profile.phone}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                      <input
                        type="text"
                        value={profile.title}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-slate-50"
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Employee ID</label>
                      <input
                        type="text"
                        value={profile.employeeId}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-slate-50"
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Department</label>
                      <input
                        type="text"
                        value={profile.department}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-slate-50"
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Assigned Region</label>
                      <input
                        type="text"
                        value={profile.region}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-slate-50"
                        disabled
                      />
                    </div>
                  </div>
                </SectionCard>

                <SectionCard title="Role & Permissions" description="Your access level and capabilities">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div>
                        <p className="font-medium text-blue-900">Senior Financial Auditor</p>
                        <p className="text-sm text-blue-700">Full audit capabilities with team lead privileges</p>
                      </div>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Active</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Check className="w-4 h-4 text-green-500" />
                        Review and process payments
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Check className="w-4 h-4 text-green-500" />
                        Access provider history
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Check className="w-4 h-4 text-green-500" />
                        Submit audit decisions
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Check className="w-4 h-4 text-green-500" />
                        View team metrics
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Check className="w-4 h-4 text-green-500" />
                        QA team decisions
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Check className="w-4 h-4 text-green-500" />
                        Generate reports
                      </div>
                    </div>
                  </div>
                </SectionCard>
              </>
            )}

            {/* Notifications Section */}
            {activeSection === 'notifications' && (
              <>
                <SectionCard title="Email Notifications" description="Configure which emails you receive">
                  <SettingRow
                    label="New case assignments"
                    description="Get notified when cases are assigned to your queue"
                  >
                    <Toggle
                      enabled={notifications.emailNewAssignments}
                      onChange={(v) => setNotifications({ ...notifications, emailNewAssignments: v })}
                    />
                  </SettingRow>
                  <SettingRow
                    label="High priority alerts"
                    description="Immediate notification for high-priority cases"
                  >
                    <Toggle
                      enabled={notifications.emailHighPriority}
                      onChange={(v) => setNotifications({ ...notifications, emailHighPriority: v })}
                    />
                  </SettingRow>
                  <SettingRow
                    label="Daily digest"
                    description="Summary of queue status and pending items each morning"
                  >
                    <Toggle
                      enabled={notifications.emailDailyDigest}
                      onChange={(v) => setNotifications({ ...notifications, emailDailyDigest: v })}
                    />
                  </SettingRow>
                  <SettingRow
                    label="Weekly performance report"
                    description="Weekly summary of your audit metrics and team performance"
                  >
                    <Toggle
                      enabled={notifications.emailWeeklyReport}
                      onChange={(v) => setNotifications({ ...notifications, emailWeeklyReport: v })}
                    />
                  </SettingRow>
                  <SettingRow
                    label="SLA warnings"
                    description="Alert when cases approach SLA deadlines"
                  >
                    <Toggle
                      enabled={notifications.emailSLAWarnings}
                      onChange={(v) => setNotifications({ ...notifications, emailSLAWarnings: v })}
                    />
                  </SettingRow>
                </SectionCard>

                <SectionCard title="In-App Notifications" description="Notifications within the application">
                  <SettingRow
                    label="New assignments"
                    description="Show notification badge for new queue items"
                  >
                    <Toggle
                      enabled={notifications.inAppNewAssignments}
                      onChange={(v) => setNotifications({ ...notifications, inAppNewAssignments: v })}
                    />
                  </SettingRow>
                  <SettingRow
                    label="High priority cases"
                    description="Prominent alerts for high-priority items"
                  >
                    <Toggle
                      enabled={notifications.inAppHighPriority}
                      onChange={(v) => setNotifications({ ...notifications, inAppHighPriority: v })}
                    />
                  </SettingRow>
                  <SettingRow
                    label="Mentions and comments"
                    description="When team members mention you in case notes"
                  >
                    <Toggle
                      enabled={notifications.inAppMentions}
                      onChange={(v) => setNotifications({ ...notifications, inAppMentions: v })}
                    />
                  </SettingRow>
                  <SettingRow
                    label="System updates"
                    description="Announcements about new features and maintenance"
                  >
                    <Toggle
                      enabled={notifications.inAppSystemUpdates}
                      onChange={(v) => setNotifications({ ...notifications, inAppSystemUpdates: v })}
                    />
                  </SettingRow>
                </SectionCard>

                <SectionCard title="Quiet Hours" description="Pause notifications during specified hours">
                  <SettingRow
                    label="Enable quiet hours"
                    description="Suppress non-critical notifications during set times"
                  >
                    <Toggle
                      enabled={notifications.quietHoursEnabled}
                      onChange={(v) => setNotifications({ ...notifications, quietHoursEnabled: v })}
                    />
                  </SettingRow>
                  {notifications.quietHoursEnabled && (
                    <div className="flex items-center gap-4 pt-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Start</label>
                        <input
                          type="time"
                          value={notifications.quietHoursStart}
                          onChange={(e) => setNotifications({ ...notifications, quietHoursStart: e.target.value })}
                          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                        />
                      </div>
                      <span className="text-slate-400 mt-6">to</span>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">End</label>
                        <input
                          type="time"
                          value={notifications.quietHoursEnd}
                          onChange={(e) => setNotifications({ ...notifications, quietHoursEnd: e.target.value })}
                          className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  )}
                </SectionCard>
              </>
            )}

            {/* Display Section */}
            {activeSection === 'display' && (
              <>
                <SectionCard title="Appearance" description="Customize the look and feel">
                  <SettingRow
                    label="Dark Mode"
                    description="Reduce eye strain with a darker color scheme"
                  >
                    <Toggle enabled={isDarkMode} onChange={toggleTheme} />
                  </SettingRow>

                  <SettingRow label="Theme" description="Choose your preferred color scheme">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setTheme('light')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition ${
                          !isDarkMode
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : `${theme.border} ${theme.textSecondary} ${theme.bgHover}`
                        }`}
                      >
                        <Sun className="w-4 h-4" /> Light
                      </button>
                      <button
                        onClick={() => setTheme('dark')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition ${
                          isDarkMode
                            ? 'border-blue-500 bg-blue-600 text-white'
                            : `${theme.border} ${theme.textSecondary} ${theme.bgHover}`
                        }`}
                      >
                        <Moon className="w-4 h-4" /> Dark
                      </button>
                    </div>
                  </SettingRow>
                  <SettingRow label="Display density" description="Adjust spacing and sizing of elements">
                    <select
                      value={display.density}
                      onChange={(e) => setDisplay({ ...display, density: e.target.value })}
                      className={`px-3 py-2 ${theme.input} rounded-lg text-sm`}
                    >
                      <option value="compact">Compact</option>
                      <option value="comfortable">Comfortable</option>
                      <option value="spacious">Spacious</option>
                    </select>
                  </SettingRow>
                </SectionCard>

                {isDarkMode && (
                  <div className={`p-4 rounded-xl ${theme.card} border flex items-start gap-3`}>
                    <Moon className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div>
                      <p className={`text-sm font-medium ${theme.textPrimary}`}>Dark Mode Active</p>
                      <p className={`text-xs ${theme.textTertiary} mt-1`}>
                        You're using ARGUS in dark mode. This setting reduces eye strain during extended audit sessions.
                      </p>
                    </div>
                  </div>
                )}

                <SectionCard title="Queue Display" description="Customize how the review queue appears">
                  <SettingRow label="Default queue filter" description="Initial filter when opening dashboard">
                    <select
                      value={display.defaultQueueView}
                      onChange={(e) => setDisplay({ ...display, defaultQueueView: e.target.value })}
                      className={`px-3 py-2 ${theme.input} rounded-lg text-sm`}
                    >
                      <option value="all">All Priorities</option>
                      <option value="high">High Priority Only</option>
                      <option value="assigned">Assigned to Me</option>
                      <option value="unassigned">Unassigned</option>
                    </select>
                  </SettingRow>
                  <SettingRow label="Items per page" description="Number of cases shown per page">
                    <select
                      value={display.itemsPerPage}
                      onChange={(e) => setDisplay({ ...display, itemsPerPage: e.target.value })}
                      className={`px-3 py-2 ${theme.input} rounded-lg text-sm`}
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </SettingRow>
                  <SettingRow
                    label="Show risk score colors"
                    description="Color-code risk scores by severity"
                  >
                    <Toggle
                      enabled={display.showRiskScoreColors}
                      onChange={(v) => setDisplay({ ...display, showRiskScoreColors: v })}
                    />
                  </SettingRow>
                  <SettingRow
                    label="Highlight flagged procedure codes"
                    description="Visually emphasize codes with violations"
                  >
                    <Toggle
                      enabled={display.highlightFlaggedCodes}
                      onChange={(v) => setDisplay({ ...display, highlightFlaggedCodes: v })}
                    />
                  </SettingRow>
                </SectionCard>

                <SectionCard title="Regional Settings" description="Date, time, and format preferences">
                  <SettingRow label="Date format" description="How dates are displayed throughout the app">
                    <select
                      value={display.dateFormat}
                      onChange={(e) => setDisplay({ ...display, dateFormat: e.target.value })}
                      className={`px-3 py-2 ${theme.input} rounded-lg text-sm`}
                    >
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </SettingRow>
                  <SettingRow label="Timezone" description="Your local timezone for scheduling">
                    <select
                      value={display.timezone}
                      onChange={(e) => setDisplay({ ...display, timezone: e.target.value })}
                      className={`px-3 py-2 ${theme.input} rounded-lg text-sm`}
                    >
                      <option value="America/New_York">Eastern Time (ET)</option>
                      <option value="America/Chicago">Central Time (CT)</option>
                      <option value="America/Denver">Mountain Time (MT)</option>
                      <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    </select>
                  </SettingRow>
                </SectionCard>
              </>
            )}

            {/* Workflow Section */}
            {activeSection === 'workflow' && (
              <>
                <SectionCard title="Queue Management" description="Control how cases are assigned to you">
                  <SettingRow
                    label="Auto-assign cases"
                    description="Automatically receive new cases based on capacity"
                  >
                    <Toggle
                      enabled={workflow.autoAssignEnabled}
                      onChange={(v) => setWorkflow({ ...workflow, autoAssignEnabled: v })}
                    />
                  </SettingRow>
                  <SettingRow label="Maximum queue size" description="Maximum cases in your active queue">
                    <select
                      value={workflow.maxQueueSize}
                      onChange={(e) => setWorkflow({ ...workflow, maxQueueSize: e.target.value })}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    >
                      <option value="25">25 cases</option>
                      <option value="50">50 cases</option>
                      <option value="75">75 cases</option>
                      <option value="100">100 cases</option>
                      <option value="unlimited">Unlimited</option>
                    </select>
                  </SettingRow>
                  <SettingRow label="Priority weighting" description="How case priority affects assignment">
                    <select
                      value={workflow.priorityWeighting}
                      onChange={(e) => setWorkflow({ ...workflow, priorityWeighting: e.target.value })}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    >
                      <option value="high_first">High Priority First</option>
                      <option value="balanced">Balanced Mix</option>
                      <option value="fifo">First In, First Out</option>
                    </select>
                  </SettingRow>
                </SectionCard>

                <SectionCard title="SLA & Reminders" description="Deadline tracking and alerts">
                  <SettingRow label="SLA reminder" description="Minutes before deadline to show warning">
                    <select
                      value={workflow.slaReminderMinutes}
                      onChange={(e) => setWorkflow({ ...workflow, slaReminderMinutes: e.target.value })}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    >
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="120">2 hours</option>
                      <option value="240">4 hours</option>
                      <option value="480">8 hours</option>
                    </select>
                  </SettingRow>
                </SectionCard>

                <SectionCard title="Decision Workflow" description="Customize the audit decision process">
                  <SettingRow
                    label="Auto-save notes"
                    description="Automatically save notes as you type"
                  >
                    <Toggle
                      enabled={workflow.autoSaveNotes}
                      onChange={(v) => setWorkflow({ ...workflow, autoSaveNotes: v })}
                    />
                  </SettingRow>
                  <SettingRow
                    label="Require notes on deny"
                    description="Notes are mandatory when denying a payment"
                  >
                    <Toggle
                      enabled={workflow.requireNotesOnDeny}
                      onChange={(v) => setWorkflow({ ...workflow, requireNotesOnDeny: v })}
                    />
                  </SettingRow>
                  <SettingRow
                    label="Require category on deny"
                    description="Issue category is mandatory when denying"
                  >
                    <Toggle
                      enabled={workflow.requireCategoryOnDeny}
                      onChange={(v) => setWorkflow({ ...workflow, requireCategoryOnDeny: v })}
                    />
                  </SettingRow>
                  <SettingRow
                    label="Show similar cases"
                    description="Display related historical cases for reference"
                  >
                    <Toggle
                      enabled={workflow.showSimilarCases}
                      onChange={(v) => setWorkflow({ ...workflow, showSimilarCases: v })}
                    />
                  </SettingRow>
                  <SettingRow label="Default confidence level" description="Pre-selected confidence for decisions">
                    <select
                      value={workflow.defaultConfidence}
                      onChange={(e) => setWorkflow({ ...workflow, defaultConfidence: e.target.value })}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    >
                      <option value="HIGH">High</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="LOW">Low</option>
                    </select>
                  </SettingRow>
                </SectionCard>
              </>
            )}

            {/* Security Section */}
            {activeSection === 'security' && (
              <>
                <SectionCard title="Multi-Factor Authentication" description="Additional security for your account">
                  <SettingRow
                    label="MFA enabled"
                    description="Require second factor for login"
                  >
                    <Toggle
                      enabled={security.mfaEnabled}
                      onChange={(v) => setSecurity({ ...security, mfaEnabled: v })}
                    />
                  </SettingRow>
                  {security.mfaEnabled && (
                    <SettingRow label="MFA method" description="Your preferred authentication method">
                      <select
                        value={security.mfaMethod}
                        onChange={(e) => setSecurity({ ...security, mfaMethod: e.target.value })}
                        className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      >
                        <option value="authenticator">Authenticator App</option>
                        <option value="sms">SMS Code</option>
                        <option value="piv">PIV/CAC Card</option>
                        <option value="email">Email Code</option>
                      </select>
                    </SettingRow>
                  )}
                </SectionCard>

                <SectionCard title="Session Settings" description="Control your login sessions">
                  <SettingRow label="Session timeout" description="Auto-logout after inactivity">
                    <select
                      value={security.sessionTimeout}
                      onChange={(e) => setSecurity({ ...security, sessionTimeout: e.target.value })}
                      className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="120">2 hours</option>
                    </select>
                  </SettingRow>
                  <SettingRow
                    label="Remember this device"
                    description="Skip MFA on trusted devices"
                  >
                    <Toggle
                      enabled={security.rememberDevice}
                      onChange={(v) => setSecurity({ ...security, rememberDevice: v })}
                    />
                  </SettingRow>
                  <SettingRow
                    label="Login notifications"
                    description="Email alert on new device login"
                  >
                    <Toggle
                      enabled={security.loginNotifications}
                      onChange={(v) => setSecurity({ ...security, loginNotifications: v })}
                    />
                  </SettingRow>
                </SectionCard>

                <SectionCard title="Password" description="Manage your account password">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-800">Current password</p>
                      <p className="text-xs text-slate-500">Last changed 45 days ago</p>
                    </div>
                    <button className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition">
                      Change Password
                    </button>
                  </div>
                </SectionCard>

                <SectionCard title="Active Sessions" description="Devices currently logged in">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Monitor className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="text-sm font-medium text-slate-800">Windows PC - Chrome</p>
                          <p className="text-xs text-slate-500">Washington, DC • Current session</p>
                        </div>
                      </div>
                      <span className="text-xs text-green-600 font-medium">Active now</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Smartphone className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm font-medium text-slate-800">iPhone - Safari</p>
                          <p className="text-xs text-slate-500">Richmond, VA • 2 days ago</p>
                        </div>
                      </div>
                      <button className="text-xs text-red-600 hover:text-red-700 font-medium">
                        Revoke
                      </button>
                    </div>
                  </div>
                </SectionCard>
              </>
            )}

            {/* Integrations Section */}
            {activeSection === 'integrations' && (
              <>
                <SectionCard title="Connected Systems" description="External systems linked to your account">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Globe className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">VA Portal</p>
                          <p className="text-sm text-slate-500">Access beneficiary and service data</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1 text-sm text-green-600">
                          <Check className="w-4 h-4" />
                          Connected
                        </span>
                        <button className="text-sm text-slate-500 hover:text-slate-700">Configure</button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Building className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">Vendor Portal</p>
                          <p className="text-sm text-slate-500">Provider billing and claims data</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1 text-sm text-green-600">
                          <Check className="w-4 h-4" />
                          Connected
                        </span>
                        <button className="text-sm text-slate-500 hover:text-slate-700">Configure</button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Database className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">IDEA Analytics</p>
                          <p className="text-sm text-slate-500">Statistical sampling and analysis</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1 text-sm text-green-600">
                          <Check className="w-4 h-4" />
                          Connected
                        </span>
                        <button className="text-sm text-slate-500 hover:text-slate-700">Configure</button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">SharePoint</p>
                          <p className="text-sm text-slate-500">Document storage and authorization data</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1 text-sm text-green-600">
                          <Check className="w-4 h-4" />
                          Connected
                        </span>
                        <button className="text-sm text-slate-500 hover:text-slate-700">Configure</button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Download className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">SFTP Server</p>
                          <p className="text-sm text-slate-500">Invoice and report file transfers</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1 text-sm text-green-600">
                          <Check className="w-4 h-4" />
                          Connected
                        </span>
                        <button className="text-sm text-slate-500 hover:text-slate-700">Configure</button>
                      </div>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard title="API Access" description="Manage programmatic access tokens">
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-800">Personal Access Token</p>
                        <p className="text-sm text-slate-500">For automated scripts and integrations</p>
                      </div>
                      <button className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition">
                        Generate Token
                      </button>
                    </div>
                  </div>
                </SectionCard>
              </>
            )}

            {/* Data & Privacy Section */}
            {activeSection === 'data' && (
              <>
                <SectionCard title="Data Export" description="Download your activity and audit data">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">Export Audit History</p>
                        <p className="text-sm text-slate-500">Download all your audit decisions and notes</p>
                      </div>
                      <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition">
                        <Download className="w-4 h-4" />
                        Export CSV
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-4 border border-slate-200 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">Export Activity Log</p>
                        <p className="text-sm text-slate-500">Record of all system actions and access</p>
                      </div>
                      <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition">
                        <Download className="w-4 h-4" />
                        Export CSV
                      </button>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard title="Audit Log" description="Recent activity on your account">
                  <div className="space-y-3">
                    {[
                      { action: 'Login from new device', time: '2 hours ago', icon: Key },
                      { action: 'Password changed', time: '45 days ago', icon: Lock },
                      { action: 'MFA method updated', time: '2 months ago', icon: Smartphone },
                      { action: 'Profile information updated', time: '3 months ago', icon: User }
                    ].map((log, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <log.icon className="w-4 h-4 text-slate-400" />
                        <div className="flex-1">
                          <p className="text-sm text-slate-700">{log.action}</p>
                        </div>
                        <span className="text-xs text-slate-400">{log.time}</span>
                      </div>
                    ))}
                  </div>
                  <button className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium">
                    View full audit log →
                  </button>
                </SectionCard>

                <SectionCard title="Privacy" description="Control your data and privacy settings">
                  <SettingRow
                    label="Show last login"
                    description="Display last login time on dashboard"
                  >
                    <Toggle
                      enabled={security.showLastLogin}
                      onChange={(v) => setSecurity({ ...security, showLastLogin: v })}
                    />
                  </SettingRow>
                </SectionCard>

                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-red-800">Danger Zone</h4>
                      <p className="text-sm text-red-600 mt-1">
                        Account deletion is not available for government accounts. Contact your administrator for account changes.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      </div>

      <Footer />
    </div>
  );
};

export default SettingsPage;
